import { useContext, useEffect, useMemo, useRef, useState, type FC } from 'react';
import {
  ArrowRight, Check, CircleHelp, Clipboard, Download, FileText, Lightbulb,
  Pencil, RotateCcw, ShieldCheck, Sparkles,
} from 'lucide-react';
import type { A2uiNodeViewProps } from '@/app/lib/a2ui/catalog.tsx';
import { arr, str } from '@/app/lib/a2ui/props.ts';
import { A2uiSurfaceContext } from '@/app/lib/a2ui/surface-context.ts';
import { stageFieldDraft } from '@/app/lib/a2ui/field-draft.ts';
import { isRecord } from '@/app/lib/util/type-guards.ts';
import { SuggestedReply } from '@/app/lib/a2ui/blocks/SuggestedReply.tsx';
import { SurfaceHeadingLevel } from '@/app/lib/a2ui/blocks/heading-context.ts';
import { Button } from '@/app/lib/shadcdn/button.tsx';
import { SITE_CONFIG } from '../site-config.ts';

const DEMO_NOTICE = 'Fictional portfolio demo. Use sample details only. No quotes, bookings, or messages are sent.';

const CORE_FIELD_IDS = new Set([
  'projectGoal', 'intendedUsers', 'projectTrack', 'recommendedSolution',
  'deliverables', 'exclusions', 'existingToolsAndContent', 'budgetRange', 'desiredTimeline',
]);

function record(value: unknown): Record<string, unknown> {
  return isRecord(value) ? value : {};
}
function text(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

type BriefField = {
  id: string;
  label: string;
  value: string;
  status: 'confirmed' | 'needsInput' | 'assumption';
};
type ExampleCard = {
  id: 'websiteRefresh' | 'internalTool' | 'aiWorkflow';
  track: string;
  title: string;
  description: string;
  buttonLabel: string;
  sampleContext: string;
};

function parseFields(value: unknown): BriefField[] {
  return arr(value).filter(isRecord).map((item) => ({
    id: text(item.id),
    label: text(item.label),
    value: text(item.value),
    status: item.status === 'confirmed' || item.status === 'assumption' ? item.status : 'needsInput',
  }));
}
function withoutRepeatedPrefix(value: string, prefix: string): string {
  const content = value.trim();
  const tag = prefix.trim();
  if (tag && content.toLocaleLowerCase().startsWith(tag.toLocaleLowerCase())) {
    return content.slice(tag.length).replace(/^[\s:—–-]+/u, '');
  }
  const marker = content.match(/^([\p{L}\p{M}]+)\s+([\p{L}\p{M}]+)\s*[:—–-]\s*/u);
  const labelWords = new Set(tag.match(/[\p{L}\p{M}]+/gu)?.map((word) => word.toLocaleLowerCase()) ?? []);
  if (marker && labelWords.has(marker[1].toLocaleLowerCase()) && labelWords.has(marker[2].toLocaleLowerCase())) {
    return content.slice(marker[0].length).trimStart();
  }
  return content;
}

function parseExamples(value: unknown): ExampleCard[] {
  return arr(value).filter(isRecord).flatMap((item) => {
    if (item.id !== 'websiteRefresh' && item.id !== 'internalTool' && item.id !== 'aiWorkflow') return [];
    return [{
      id: item.id, track: text(item.track), title: text(item.title),
      description: text(item.description), buttonLabel: text(item.buttonLabel),
      sampleContext: text(item.sampleContext),
    }];
  });
}
function exportText(fields: BriefField[], labels: Record<string, unknown>, notice: string): string {
  const status = (field: BriefField) => field.status === 'confirmed'
    ? text(labels.confirmedLabel)
    : field.status === 'assumption' ? text(labels.assumptionLabel) : text(labels.needsInputLabel);
  const missing = text(labels.notSharedLabel);
  return [
    SITE_CONFIG.brandName, text(labels.finalHeading), notice, '',
    ...fields.flatMap((field) => [
      `${field.label} · ${status(field)}`,
      withoutRepeatedPrefix(field.value, status(field)) || missing,
      '',
    ]),
    text(labels.finalNotice),
  ].filter((line, index, all) => line !== '' || (index > 0 && all[index - 1] !== '')).join('\n');
}

export const BriefForgeWorkspace: FC<A2uiNodeViewProps> = ({ node }) => {
  const api = useContext(A2uiSurfaceContext);
  const headingLevel = useContext(SurfaceHeadingLevel);
  const Heading = headingLevel === 1 ? 'h1' : 'h2';
  const labels = record(node.props.labels);
  const fields = useMemo(() => parseFields(node.props.fields), [node.props.fields]);
  const examples = useMemo(() => parseExamples(node.props.examples), [node.props.examples]);
  const [editingId, setEditingId] = useState('');
  const [draftValues, setDraftValues] = useState<Record<string, string>>({});
  const [feedback, setFeedback] = useState('');
  const lastAccepted = useRef(new Map<string, { value: string; status: BriefField['status'] }>());
  const confirmedCount = fields.filter((field) =>
    CORE_FIELD_IDS.has(field.id) && field.status === 'confirmed' && field.value.trim(),
  ).length;
  const number = (value: number) => {
    try { return new Intl.NumberFormat(document.documentElement.lang || undefined).format(value); }
    catch { return String(value); }
  };
  const progressText = `${number(confirmedCount)} / ${number(9)} ${text(labels.readinessSuffix)}`;

  useEffect(() => {
    const previous = lastAccepted.current;
    const next = new Map<string, { value: string; status: BriefField['status'] }>();
    for (const field of fields) {
      const before = previous.get(field.id);
      if (before && (before.value !== field.value || before.status !== field.status)) {
        setDraftValues((current) => {
          if (!(field.id in current) || editingId === field.id) return current;
          const updated = { ...current };
          delete updated[field.id];
          return updated;
        });
      }
      next.set(field.id, { value: field.value, status: field.status });
    }
    lastAccepted.current = next;
  }, [fields, editingId]);

  const briefText = exportText(fields, labels, DEMO_NOTICE);
  const beginEdit = (field: BriefField) => {
    setFeedback('');
    setDraftValues((current) => ({ ...current, [field.id]: current[field.id] ?? field.value }));
    setEditingId(field.id);
  };
  const updateField = (field: BriefField, value: string) => {
    setDraftValues((current) => ({ ...current, [field.id]: value }));
    stageFieldDraft(api, {
      id: `brief-forge-${field.id}`,
      label: field.label,
      instruction: text(labels.editInstruction),
      context: { fieldId: field.id, artifact: 'project-brief' },
      fields: [{ id: field.id, label: field.label, value }],
    });
  };
  const statusMeta = (status: BriefField['status']) => status === 'confirmed'
    ? { label: text(labels.confirmedLabel), Icon: Check }
    : status === 'assumption'
      ? { label: text(labels.assumptionLabel), Icon: Lightbulb }
      : { label: text(labels.needsInputLabel), Icon: CircleHelp };

  const copyBrief = async () => {
    setFeedback('');
    try {
      if (!navigator.clipboard?.writeText) throw new Error('clipboard unavailable');
      await navigator.clipboard.writeText(briefText);
      setFeedback(text(labels.copySuccess));
    } catch { setFeedback(text(labels.copyFailure)); }
  };
  const downloadBrief = () => {
    setFeedback('');
    try {
      const href = URL.createObjectURL(new Blob([briefText], { type: 'text/plain;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = href;
      link.download = 'brief-forge-project-brief.txt';
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(href), 0);
      setFeedback(text(labels.downloadSuccess));
    } catch { setFeedback(text(labels.copyFailure)); }
  };

  return (
    <main
      className="brief-forge-workspace space-y-8 py-4 sm:py-8"
      data-examples-visible={node.props.examplesVisible === true}
    >
      <header className="relative max-w-4xl space-y-6">
        <p className="bf-wordmark inline-flex items-center gap-3 text-sm font-medium" translate="no">
          <svg className="h-12 w-12 shrink-0" viewBox="0 0 64 64" fill="none" aria-hidden="true">
            <g className="bf-emblem-arch" strokeWidth="1.35" strokeLinecap="round" strokeLinejoin="round">
              <path d="M8 58V30c0-7 4-12 12-19L32 2l12 9c8 7 12 12 12 19v28H8Z" />
              <path d="M18 58V31c0-5 3-9 8-14l6-5 6 5c5 5 8 9 8 14v27" />
              <path d="M26 58V32l6-8 6 8v26M24 42h16M32 33v18" />
              <path d="m15 26 17-15 17 15" />
            </g>
            <path className="bf-emblem-star" d="m15 23 .9 2.2 2.3.8-2.3.9-.9 2.2-.8-2.2-2.3-.9 2.3-.8.8-2.2Z" />
            <path className="bf-emblem-star" d="m49 23 .9 2.2 2.3.8-2.3.9-.9 2.2-.8-2.2-2.3-.9 2.3-.8.8-2.2Z" />
            <circle className="bf-emblem-star" cx="32" cy="11" r="1.4" />
          </svg>
          <span className="flex flex-col gap-1">
            <span className="bf-wordmark-kicker text-base leading-none">MAGNIFICAT</span>
            <span className="bf-wordmark-name text-xs leading-none">GEARZ · BRIEFFORGE</span>
          </span>
        </p>
        <div className="space-y-4">
          <Heading id="brief-forge-title" className="bf-display max-w-4xl text-4xl leading-[1.06] text-foreground sm:text-5xl lg:text-6xl">
            {str(node.props.headline)}
          </Heading>
          <p className="max-w-2xl text-base leading-7 text-muted-foreground sm:text-lg sm:leading-8">
            {str(node.props.message)}
          </p>
        </div>
        <p className="flex max-w-2xl items-start gap-3 rounded-lg border border-warning/40 bg-warning/10 px-4 py-3 text-base leading-6 text-foreground">
          <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-warning" aria-hidden="true" />
          <span>{DEMO_NOTICE}</span>
        </p>
      </header>

      <div className="brief-forge-grid grid min-w-0 grid-cols-1 items-start gap-8 lg:gap-10">
        <section aria-labelledby="brief-forge-examples" className="min-w-0 space-y-5">
          {node.props.examplesVisible === true ? (
            <>
              <div className="space-y-2">
                <h2 id="brief-forge-examples" className="text-xl font-semibold leading-snug text-foreground">
                  {text(labels.exampleHeading)}
                </h2>
                <p className="text-base leading-7 text-muted-foreground">{text(labels.exampleHelper)}</p>
              </div>
              <div className="divide-y divide-border border-y border-border">
                {examples.map((example) => {
                  const ExampleIcon = example.id === 'websiteRefresh' ? FileText
                    : example.id === 'internalTool' ? Clipboard : Sparkles;
                  return (
                    <article key={example.id} className="grid gap-4 py-5 sm:grid-cols-[2rem_minmax(0,1fr)] sm:gap-4">
                      <ExampleIcon className="mt-1 h-5 w-5 text-primary" aria-hidden="true" />
                      <div className="min-w-0 space-y-3">
                        <h3 className="text-lg font-semibold leading-6 text-foreground">{example.title}</h3>
                        <p className="text-base leading-7 text-muted-foreground">{example.description}</p>
                        {example.sampleContext ? (
                          <p className="border-s-2 border-warning/60 ps-3 text-base leading-7 text-foreground">
                            <span className="font-medium text-warning">{text(labels.sampleLabel)}: </span>
                            {withoutRepeatedPrefix(example.sampleContext, text(labels.sampleLabel))}
                          </p>
                        ) : null}
                        <SuggestedReply
                          type="button"
                          disabled={!api}
                          onClick={() => api?.dispatch(
                            'startProjectTrack',
                            { exampleId: example.id, track: example.track, sampleContext: example.sampleContext },
                            example.buttonLabel,
                          )}
                          className="min-h-11 w-full rounded-lg border border-border px-3 py-2 text-base hover:border-primary/60 hover:bg-accent/70 sm:w-fit"
                        >
                          {example.buttonLabel}
                        </SuggestedReply>
                      </div>
                    </article>
                  );
                })}
              </div>
            </>
          ) : (
            <p className="max-w-lg border-s-2 border-primary/60 ps-4 text-base leading-7 text-muted-foreground">
              {text(labels.exampleHelper)}
            </p>
          )}
        </section>

        <section aria-labelledby="brief-forge-live-brief" className="min-w-0 rounded-2xl border border-border bg-card/90 p-4 shadow-elevated sm:p-6 lg:p-7">
          <div className="flex flex-wrap items-start justify-between gap-4 border-b border-border pb-5">
            <div className="min-w-0 space-y-2">
              <h2 id="brief-forge-live-brief" className="text-xl font-semibold leading-snug text-card-foreground">
                {text(labels.briefHeading)}
              </h2>
  
            </div>
            <Button
              type="button" variant="ghost" size="surface" aria-label={text(labels.startOverLabel)}
              disabled={!api}
              onClick={() => api?.dispatch('startNewBrief', { intent: 'new-project-brief' }, text(labels.startOverLabel))}
              className="min-h-11 gap-2 rounded-lg border border-border px-3 text-base hover:border-primary/50 hover:bg-accent/70"
            >
              <RotateCcw className="h-4 w-4 shrink-0" aria-hidden="true" />
              {text(labels.startOverLabel)}
            </Button>
          </div>

          <div className="space-y-3 border-b border-border py-5">
            <div className="flex items-baseline justify-between gap-4 text-base">
              <span className="min-w-0 leading-6 text-muted-foreground">{text(labels.readinessLabel)}</span>
              <span className="shrink-0 tabular-nums font-semibold text-foreground">{progressText}</span>
            </div>
            <progress className="bf-readiness-bar block h-2 w-full overflow-hidden rounded-full"
              aria-label={`${text(labels.readinessLabel)}: ${progressText}`} value={confirmedCount} max={9} />
          </div>

          <p className="border-b border-border py-4 text-base leading-7 text-muted-foreground">
            {text(labels.editInstruction)}
          </p>

          <div className="divide-y divide-border">
            {fields.map((field) => {
              const { label, Icon } = statusMeta(field.status);
              const editing = editingId === field.id;
              const inputId = `brief-forge-field-${field.id}`;
              return (
                <article key={field.id} className="min-w-0 py-4">
                  <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-2">
                    <h3 className="min-w-0 flex-1 text-base font-semibold leading-6 text-card-foreground">{field.label}</h3>
                    <span data-status={field.status} className="bf-status inline-flex shrink-0 items-center gap-2 rounded-full border px-3 py-1 text-base leading-5">
                      <Icon className="h-4 w-4" aria-hidden="true" />{label}
                    </span>
                  </div>
                  {editing ? (
                    <div className="mt-3 space-y-2">
                      <label className="sr-only" htmlFor={inputId}>{`${text(labels.editLabel)}: ${field.label}`}</label>
                      <textarea
                        id={inputId} name={`brief-${field.id}`} rows={3}
                        className="bf-field-editor min-h-24 w-full resize-y px-3 py-2 text-base leading-7"
                        value={draftValues[field.id] ?? field.value}
                        aria-describedby={`${inputId}-hint`}
                        onChange={(event) => updateField(field, event.currentTarget.value)}
                        onBlur={() => {
                          api?.commitValue();
                          setEditingId((current) => current === field.id ? '' : current);
                        }}
                      />
                      <p id={`${inputId}-hint`} className="text-base leading-6 text-muted-foreground">
                        {text(labels.editInstruction)}
                      </p>
                    </div>
                  ) : (
                    <div className="mt-2 flex items-start justify-between gap-3">
                      <p className={`min-w-0 flex-1 whitespace-pre-wrap break-words text-base leading-7 ${field.value.trim() ? 'text-card-foreground' : 'text-muted-foreground'}`}>
                        {withoutRepeatedPrefix(field.value, label) || text(labels.notSharedLabel)}
                      </p>
                      <Button
                        type="button" variant="ghost" size="icon" disabled={!api?.addToMessage}
                        aria-label={`${text(labels.editLabel)}: ${field.label}`}
                        title={`${text(labels.editLabel)}: ${field.label}`}
                        onClick={() => beginEdit(field)}
                        className="mt-0.5 h-11 w-11 shrink-0 rounded-lg border border-border text-foreground hover:border-primary/60 hover:bg-accent/70 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-primary"
                      >
                        <Pencil className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    </div>
                  )}
                </article>
              );
            })}
          </div>

          {node.props.canGenerate === true && !node.props.finalized ? (
            <div className="space-y-3 border-t border-border pt-5">
              <p className="text-base leading-7 text-muted-foreground">{text(labels.generateHelper)}</p>
              <Button type="button" variant="default" size="surface" disabled={!api}
                onClick={() => api?.dispatch('requestFinalBrief', { confirmedCount }, text(labels.generateLabel))}
                className="min-h-12 w-full rounded-xl px-5 text-base font-semibold sm:w-auto">
                <Sparkles className="me-2 h-4 w-4" aria-hidden="true" />{text(labels.generateLabel)}
                <ArrowRight className="ms-2 h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
          ) : null}

          {node.props.finalized === true ? (
            <div className="space-y-4 border-t border-border pt-5">
              <div className="space-y-2">
                <h3 className="bf-display text-2xl leading-tight text-card-foreground">{text(labels.finalHeading)}</h3>
                <p className="text-base leading-7 text-muted-foreground">{text(labels.finalNotice)}</p>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button type="button" variant="outline" size="surface" onClick={copyBrief} className="min-h-11 rounded-lg px-4 text-base">
                  <Clipboard className="me-2 h-4 w-4" aria-hidden="true" />{text(labels.copyLabel)}
                </Button>
                <Button type="button" variant="outline" size="surface" onClick={downloadBrief} className="min-h-11 rounded-lg px-4 text-base">
                  <Download className="me-2 h-4 w-4" aria-hidden="true" />{text(labels.downloadLabel)}
                </Button>
              </div>
              {feedback ? <p role="status" className="text-base leading-6 text-muted-foreground">{feedback}</p> : null}
            </div>
          ) : null}
        </section>
      </div>
    </main>
  );
};
